import { getFormatDateFromStoreDate } from 'shared-components';
import {
  AccommodationArrangedStatusEnum,
  EventInfoStatus,
  IStudentRow,
  RecordedInterviewSystemStatus,
  TravelStatusEnum,
  IApplicantRow,
} from 'types';
import {
  getDisplayRecordedInterviewsAllowed,
  getDisplayScheduledInterviewsAllowed,
  getFrom,
} from '../../utilities/helpers';

import { getTotalUnreadCount } from '../../utilities/getTotalUnreadMsgCount';
import { createMultiIntakeTag } from '../../utilities/tags';
import { BulkActionTypeEnum } from '../studentUsers/types';

export const facultiesToArray = (doc: any): string[] | undefined => {
  const faculties = doc.courses
    ?.map((course: { faculty: string }) => (course.faculty ? course.faculty : ''))
    .filter((val: string) => val !== '');
  if (!faculties || !faculties.length) {
    return undefined;
  }
  return faculties;
};

export const studentToRow = (doc: any, auth: any, config: any): IStudentRow | IApplicantRow => {
  if (doc.applicationId) {
    return getApplicantDashboardRow(doc);
  }
  return getStudentDashboardRow(doc, auth, config);
};

export const getApplicantDashboardRow = (doc: any) => {
  return {
    ...doc,
  };
};

export const getStudentDashboardRow = (doc: any, auth: any, config: any) => {
  // if the student is in more than one intake we need to push on the multi intakes tag
  if (doc.intakes && doc.intakes.length > 1) {
    const multiTag = createMultiIntakeTag(doc);
    doc['tags'] = [...(doc.tags ? doc.tags : []), multiTag];
  }

  const role = auth?.role;
  const isPathway = !!auth?.isPathway;
  const isPathwayStudent = !!doc.pathway;
  const isDisplayScheduledInterviewsAllowed = getDisplayScheduledInterviewsAllowed(
    role,
    isPathway,
    isPathwayStudent,
    config?.relatedPathwaySettings,
  );
  const isDisplayRecordedInterviewsAllowed = getDisplayRecordedInterviewsAllowed(
    role,
    isPathway,
    isPathwayStudent,
    config?.relatedPathwaySettings,
  );
  return {
    accommodationArrangedStatus: doc.accommodationArrangedStatus || AccommodationArrangedStatusEnum.NONE,
    accommodationTypeStatus: doc.accommodationTypeStatus ?? '',
    faculties: facultiesToArray(doc),
    regionConfigId: doc.regionConfigId,
    withAgent: doc.withAgent,
    p_key: doc.p_key,
    student_number: doc.studentNo,
    intakeId: doc.intakeId,
    offerId: doc.offerId,
    uni: doc.pathway ? `${doc.pathway} - ${doc.p_key.split('#')[0]}` : doc.p_key.split('#')[0],
    given_name: doc.givenName,
    surname: doc.surname,
    course: doc.courseName,
    courseFee: doc.courseFee || 0,
    scholarshipsApplied: doc.scholarshipsApplied || 0,
    feeAfterScholarships: doc.feeAfterScholarships || 0,
    courseFeePaidToDate: doc.courseFeePaidToDate,
    nationality: doc.nationality,
    visaStatus: doc.visaStatus || 'None',
    travelStatus: doc.travelStatus || TravelStatusEnum.None,
    travelBookedDate: doc.travelStatusBookedDate ? getFormatDateFromStoreDate(doc.travelStatusBookedDate) : '',
    travelArrivalDate: doc.travelStatusArrivalDate ? getFormatDateFromStoreDate(doc.travelStatusArrivalDate) : '',
    offerStatus: doc.offerStatus,
    courseLevelOfferStatuses: doc.conditions,
    depositAmount: doc.depositAmount || 0,
    studentDepositAmount: doc.studentDepositAmount || 0,
    agent: doc.isAgent,
    dob: doc.dob,
    email: doc.email,
    masterAgents: doc.masterAgentAccess,
    agentName: doc.agentName,
    phone: doc.phone,
    casStatusUpdatedAt: doc.casStatusUpdatedAt,
    updatedAt: doc.updatedAt,
    lastAcceptanceDate: doc?.courses?.[0]?.lastAcceptanceDate || undefined,
    lastTouchByStudentOrAgent: doc.lastTouchByStudentOrAgent,
    country: doc.country,
    countryOfVisaApplication: doc.countryOfVisaApplication,
    questionComplete: `${doc.totalPercentageComplete || 0}%`,
    studentDetailsComplete: doc.studentDetailsComplete || 0,
    historyComplete: doc.historyComplete || 0,
    financeComplete: doc.financeComplete || 0,
    consentsComplete: doc.consentsComplete || 0,
    studentDetailsRequired: doc.studentDetailsRequired,
    historyRequired: doc.historyRequired,
    financeRequired: doc.financeRequired,
    consentsRequired: doc.consentsRequired,
    intakes: doc.intakes || [],
    isPreCASComplete: doc.isPreCASComplete,
    isPreCASRequired: doc.isPreCASRequired,
    isTasksComplete: doc.isTasksComplete,
    tasksCompleteAt: doc.tasksCompleteAt,
    totalRequired: doc.totalRequired,
    totalCompleted: doc.totalCompleted,
    docsUploaded: doc.docsUploaded || 0,
    finDocsUploaded: doc.finDocsUploaded || 0,
    docsTotal: doc.docsTotal || 0,
    finDocsTotal: doc.finDocsTotal || 0,
    documentComplete: `${(doc.docsUploaded || 0) + (doc.finDocsUploaded || 0)} / ${
      (doc.docsTotal || 0) + (doc.finDocsTotal || 0)
    }`,
    followUpDocumentComplete: `${doc.followUpDocsUploaded || 0} / ${doc.followUpDocsTotal || 0}`,
    agentDocumentComplete: `${doc.agentDocsUploaded || 0} / ${doc.agentDocsTotal || 0}`,
    successPrediction: doc.riskRating === 0 || doc.riskRating === undefined ? 'TBD' : `${doc.riskRating}%`,
    livedInUKLast12Months: doc.livedInUKLast12Months,
    tags: doc.tags || [],
    fundsMatured: doc.fundsMatured ? doc.fundsMatured : [],
    status: doc.casStatus,
    visaSupport: doc.visaSupport,
    unreadMessagesCount: role
      ? getTotalUnreadCount(
          role,
          doc.visaSupport,
          doc.withAgent,
          doc.unreadMessagesStudent,
          doc.unreadMessagesUni,
          doc.unreadMessagesStudentByUni,
          doc.unreadMessagesUniToAgent,
          doc.unreadMessagesStudentToAgent,
          doc.unreadMessagesAgentToUni,
          doc.unreadMessagesUniSecure,
          doc.unreadMessagesUniByAgent,
          doc.unreadMessagesUniByAdmin,
          doc.unreadMessagesUniToStudentByAdmin,
          doc.unreadMessagesUniToAgentByAdmin,
          doc.unreadMessagesStudentToAgentByAdmin,
          doc.unreadMessagesUniByPartner,
          doc.unreadMessagesUniToStudentByPartner,
          doc.unreadMessagesUniByImmigrationProvider,
        )
      : 0,
    pathwayUniFullName: doc.pathwayUni,
    pathway: doc.pathway,
    isNewDocuments: doc.isNewDocuments ? 'Yes' : 'No',
    lastTouch: !doc.lastTouch ? 'New' : getFrom(doc.lastTouch),
    partners: doc.partners || [],
    immigrationProviders: doc.immigrationProviders || [],
    startDate: doc.startDate,
    uniPartnerId: doc.uniPartnerId,
    courseFeeDiscount: doc.courseFeeDiscount,
    expectedDepositDate:
      doc.expectedDepositDate || doc.depositDate
        ? getFormatDateFromStoreDate(doc.expectedDepositDate ?? doc.depositDate)
        : '',
    calendlyInterviews: isDisplayScheduledInterviewsAllowed ? doc.calendlyInterviews : [],
    calendlyInterviewsInfoStatus: isDisplayScheduledInterviewsAllowed
      ? doc.calendlyInterviewsInfoStatus
      : EventInfoStatus.NONE,
    calendlyInterviewStartDate: isDisplayScheduledInterviewsAllowed ? doc.calendlyInterviewStartDate : null,
    calendlyInterviewEndDate: isDisplayScheduledInterviewsAllowed ? doc.calendlyInterviewEndDate : null,
    recordedInterviewSystemStatus: isDisplayRecordedInterviewsAllowed
      ? doc.recordedInterviewSystemStatus
      : RecordedInterviewSystemStatus.NONE,
    recordedInterviews: isDisplayRecordedInterviewsAllowed ? doc.recordedInterviews : [],
    recordedInterviewTimestamp: isDisplayRecordedInterviewsAllowed ? doc.recordedInterviewTimestamp : 0,
    dependants: doc.dependants,
    immigrationStatusShareCode: doc.immigrationStatusShareCode || '',
    shareCodeDetail: doc.shareCodeDetail || null,
    agentDetailsRequired: doc.agentDetailsRequired,
    agentDetailsComplete: doc.agentDetailsComplete,
  };
};

export const updateStudentPartners = (
  students: IStudentRow[],
  index: number,
  bulkActionType: BulkActionTypeEnum,
  partner: string,
): IStudentRow[] => {
  return students.map((student, i) => {
    if (i === index) {
      const updatedPartners =
        bulkActionType === BulkActionTypeEnum.ADD_PARTNERS
          ? [...student.partners, partner]
          : student.partners.filter(partnerItem => partnerItem !== partner);

      return {
        ...student,
        partners: updatedPartners,
      };
    }
    return student;
  });
};
